// import type { ProviderName } from '@vexalyn/shared'; // Defined locally
import * as fs from 'node:fs';
import * as path from 'node:path';

export interface PermissionRule {
  tool: string;
  action: 'allow' | 'deny' | 'ask';
  paths?: string[];
}

export interface AgentConfig {
  name: string;
  systemPrompt?: string;
  permissions?: PermissionRule[];
  model?: string;
  mode?: 'primary' | 'subagent';
  tools?: string[];
}

export interface MCPServerConfig {
  command: string;
  args: string[];
  env?: Record<string, string>;
}

export interface VexalynConfig {
  // ── Core ────────────────────────────────────────────────────────────────────
  defaultProvider: ProviderName;
  defaultModel: string;
  logLevel: 'debug' | 'info' | 'warn' | 'error';

  // ── API Keys ─────────────────────────────────────────────────────────────────
  openAiApiKey?: string;
  googleGenerativeAiApiKey?: string;
  openRouterApiKey?: string;
  groqApiKey?: string;
  qwenApiKey?: string;
  ollamaBaseUrl: string;

  // New providers
  deepseekApiKey?: string;
  zaiApiKey?: string;
  moonshotApiKey?: string;
  anthropicApiKey?: string;
  mistralApiKey?: string;
  xaiApiKey?: string;
  cerebrasApiKey?: string;
  togetherApiKey?: string;
  fireworksApiKey?: string;
  deepinfraApiKey?: string;
  siliconflowApiKey?: string;
  nvidiaNimApiKey?: string;
  cohereApiKey?: string;
  perplexityApiKey?: string;
  sambanovaApiKey?: string;
  novitaApiKey?: string;
  ai21ApiKey?: string;

  // ── Base URLs (overrideable) ──────────────────────────────────────────────────
  deepseekBaseUrl: string;
  zaiBaseUrl: string;
  moonshotBaseUrl: string;
  anthropicBaseUrl: string;
  mistralBaseUrl: string;
  xaiBaseUrl: string;
  cerebrasBaseUrl: string;
  togetherBaseUrl: string;
  fireworksBaseUrl: string;
  deepinfraBaseUrl: string;
  siliconflowBaseUrl: string;
  nvidiaNimBaseUrl: string;
  cohereBaseUrl: string;
  perplexityBaseUrl: string;
  sambanovaBaseUrl: string;
  novitaBaseUrl: string;
  ai21BaseUrl: string;

  // ── opencode.json style config ────────────────────────────────────────────────
  permissions?: PermissionRule[];
  agents?: Record<string, AgentConfig>;
  mcpServers?: Record<string, MCPServerConfig>;
  theme?: string;
  keybinds?: Record<string, string>;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ALL_PROVIDERS: ProviderName[] = [
  'openai', 'gemini', 'openrouter', 'groq', 'ollama', 'qwen',
  'deepseek', 'zai', 'moonshot', 'anthropic', 'mistral', 'xai',
  'cerebras', 'together', 'fireworks', 'deepinfra', 'siliconflow',
  'nvidia', 'cohere', 'perplexity', 'sambanova', 'novita', 'ai21',
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const levels = ['debug', 'info', 'warn', 'error'] as const;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function parseChoice(value: string | undefined, choices: readonly string[], fallback: string): string {
  return value && choices.includes(value) ? value : fallback;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function firstEnv(env: NodeJS.ProcessEnv, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = env[key]?.trim();
    if (value) return value;
  }
  return undefined;
}

export function loadConfigFromFile(workspaceRoot: string): Partial<VexalynConfig> {
  const configPath = path.resolve(workspaceRoot, 'opencode.json');
  if (!fs.existsSync(configPath)) return {};
  try {
    const content = fs.readFileSync(configPath, 'utf8');
    return JSON.parse(content);
  } catch {
    return {};
  }
}

export function loadConfig(env: NodeJS.ProcessEnv = process.env, workspaceRoot?: string): VexalynConfig {
  const fileConfig = workspaceRoot ? loadConfigFromFile(workspaceRoot) : {};
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const providers = ['openai', 'gemini', 'openrouter', 'groq', 'ollama', 'qwen',
    'deepseek', 'zai', 'moonshot', 'anthropic', 'mistral', 'xai',
    'cerebras', 'together', 'fireworks', 'deepinfra', 'siliconflow',
    'nvidia', 'cohere', 'perplexity', 'sambanova', 'novita', 'ai21'];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const levels = ['debug', 'info', 'warn', 'error'] as const;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function parseChoice(value: string | undefined, choices: readonly string[], fallback: string): string {
    return value && choices.includes(value) ? value : fallback;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function firstEnv(env: NodeJS.ProcessEnv, ...keys: string[]): string | undefined {
    for (const key of keys) {
      const value = env[key]?.trim();
      if (value) return value;
    }
    return undefined;
  }

  return {
    // Core
    defaultProvider: (fileConfig.defaultProvider ?? parseChoice(env.VEXALYN_DEFAULT_PROVIDER, ['openai', 'gemini', 'openrouter', 'groq', 'ollama', 'qwen',
      'deepseek', 'zai', 'moonshot', 'anthropic', 'mistral', 'xai',
      'cerebras', 'together', 'fireworks', 'deepinfra', 'siliconflow',
      'nvidia', 'cohere', 'perplexity', 'sambanova', 'novita', 'ai21'], 'openai')) as ProviderName,
    defaultModel:    fileConfig.defaultModel ?? env.VEXALYN_DEFAULT_MODEL ?? 'gpt-4o-mini',
    logLevel:        (fileConfig.logLevel ?? parseChoice(env.VEXALYN_LOG_LEVEL, ['debug', 'info', 'warn', 'error'], 'info')) as 'debug' | 'info' | 'warn' | 'error',

    // Existing providers
    openAiApiKey:              fileConfig.openAiApiKey ?? firstEnv(env, 'OPENAI_API_KEY', 'VEXALYN_OPENAI_API_KEY'),
    googleGenerativeAiApiKey:  fileConfig.googleGenerativeAiApiKey ?? firstEnv(env, 'GOOGLE_GENERATIVE_AI_API_KEY', 'GEMINI_API_KEY', 'GOOGLE_API_KEY', 'VEXALYN_GEMINI_API_KEY'),
    openRouterApiKey:          fileConfig.openRouterApiKey ?? firstEnv(env, 'OPENROUTER_API_KEY', 'VEXALYN_OPENROUTER_API_KEY'),
    groqApiKey:                fileConfig.groqApiKey ?? firstEnv(env, 'GROQ_API_KEY', 'VEXALYN_GROQ_API_KEY'),
    qwenApiKey:                fileConfig.qwenApiKey ?? firstEnv(env, 'QWEN_API_KEY', 'DASHSCOPE_API_KEY', 'VEXALYN_QWEN_API_KEY'),
    ollamaBaseUrl:             (fileConfig.ollamaBaseUrl ?? env.OLLAMA_BASE_URL ?? 'http://localhost:11434'),

    // New providers — API keys
    deepseekApiKey:    fileConfig.deepseekApiKey ?? firstEnv(env, 'DEEPSEEK_API_KEY', 'VEXALYN_DEEPSEEK_API_KEY'),
    zaiApiKey:         fileConfig.zaiApiKey ?? firstEnv(env, 'ZAI_API_KEY', 'GLM_API_KEY', 'VEXALYN_ZAI_API_KEY'),
    moonshotApiKey:    fileConfig.moonshotApiKey ?? firstEnv(env, 'MOONSHOT_API_KEY', 'KIMI_API_KEY', 'VEXALYN_MOONSHOT_API_KEY'),
    anthropicApiKey:   fileConfig.anthropicApiKey ?? firstEnv(env, 'ANTHROPIC_API_KEY', 'CLAUDE_API_KEY', 'VEXALYN_ANTHROPIC_API_KEY'),
    mistralApiKey:     fileConfig.mistralApiKey ?? firstEnv(env, 'MISTRAL_API_KEY', 'VEXALYN_MISTRAL_API_KEY'),
    xaiApiKey:         fileConfig.xaiApiKey ?? firstEnv(env, 'XAI_API_KEY', 'GROK_API_KEY', 'VEXALYN_XAI_API_KEY'),
    cerebrasApiKey:    fileConfig.cerebrasApiKey ?? firstEnv(env, 'CEREBRAS_API_KEY', 'VEXALYN_CEREBRAS_API_KEY'),
    togetherApiKey:    fileConfig.togetherApiKey ?? firstEnv(env, 'TOGETHER_API_KEY', 'TOGETHERAI_API_KEY', 'VEXALYN_TOGETHER_API_KEY'),
    fireworksApiKey:   fileConfig.fireworksApiKey ?? firstEnv(env, 'FIREWORKS_API_KEY', 'VEXALYN_FIREWORKS_API_KEY'),
    deepinfraApiKey:   fileConfig.deepinfraApiKey ?? firstEnv(env, 'DEEPINFRA_API_KEY', 'VEXALYN_DEEPINFRA_API_KEY'),
    siliconflowApiKey: fileConfig.siliconflowApiKey ?? firstEnv(env, 'SILICONFLOW_API_KEY', 'VEXALYN_SILICONFLOW_API_KEY'),
    nvidiaNimApiKey:   fileConfig.nvidiaNimApiKey ?? firstEnv(env, 'NVIDIA_NIM_API_KEY', 'NVIDIA_API_KEY', 'VEXALYN_NVIDIA_API_KEY'),
    cohereApiKey:      fileConfig.cohereApiKey ?? firstEnv(env, 'COHERE_API_KEY', 'VEXALYN_COHERE_API_KEY'),
    perplexityApiKey:  fileConfig.perplexityApiKey ?? firstEnv(env, 'PERPLEXITY_API_KEY', 'VEXALYN_PERPLEXITY_API_KEY'),
    sambanovaApiKey:   fileConfig.sambanovaApiKey ?? firstEnv(env, 'SAMBANOVA_API_KEY', 'VEXALYN_SAMBANOVA_API_KEY'),
    novitaApiKey:      fileConfig.novitaApiKey ?? firstEnv(env, 'NOVITA_API_KEY', 'VEXALYN_NOVITA_API_KEY'),
    ai21ApiKey:        fileConfig.ai21ApiKey ?? firstEnv(env, 'AI21_API_KEY', 'VEXALYN_AI21_API_KEY'),

    // New providers — base URLs
    deepseekBaseUrl:    (fileConfig.deepseekBaseUrl ?? env.DEEPSEEK_BASE_URL ?? 'https://api.deepseek.com/v1'),
    zaiBaseUrl:         (fileConfig.zaiBaseUrl ?? env.ZAI_BASE_URL ?? 'https://api.z.ai/api/paas/v4'),
    moonshotBaseUrl:    (fileConfig.moonshotBaseUrl ?? env.MOONSHOT_BASE_URL ?? 'https://api.moonshot.ai/v1'),
    anthropicBaseUrl:   (fileConfig.anthropicBaseUrl ?? env.ANTHROPIC_BASE_URL ?? 'https://api.anthropic.com/v1'),
    mistralBaseUrl:     (fileConfig.mistralBaseUrl ?? env.MISTRAL_BASE_URL ?? 'https://api.mistral.ai/v1'),
    xaiBaseUrl:         (fileConfig.xaiBaseUrl ?? env.XAI_BASE_URL ?? 'https://api.x.ai/v1'),
    cerebrasBaseUrl:    (fileConfig.cerebrasBaseUrl ?? env.CEREBRAS_BASE_URL ?? 'https://api.cerebras.ai/v1'),
    togetherBaseUrl:    (fileConfig.togetherBaseUrl ?? env.TOGETHER_BASE_URL ?? 'https://api.together.ai/v1'),
    fireworksBaseUrl:   (fileConfig.fireworksBaseUrl ?? env.FIREWORKS_BASE_URL ?? 'https://api.fireworks.ai/inference/v1'),
    deepinfraBaseUrl:   (fileConfig.deepinfraBaseUrl ?? env.DEEPINFRA_BASE_URL ?? 'https://api.deepinfra.com/v1/openai'),
    siliconflowBaseUrl: (fileConfig.siliconflowBaseUrl ?? env.SILICONFLOW_BASE_URL ?? 'https://api.siliconflow.com/v1'),
    nvidiaNimBaseUrl:   (fileConfig.nvidiaNimBaseUrl ?? env.NVIDIA_NIM_BASE_URL ?? 'https://integrate.api.nvidia.com/v1'),
    cohereBaseUrl:      (fileConfig.cohereBaseUrl ?? env.COHERE_BASE_URL ?? 'https://api.cohere.com/v2'),
    perplexityBaseUrl:  (fileConfig.perplexityBaseUrl ?? env.PERPLEXITY_BASE_URL ?? 'https://api.perplexity.ai'),
    sambanovaBaseUrl:   (fileConfig.sambanovaBaseUrl ?? env.SAMBANOVA_BASE_URL ?? 'https://api.sambanova.ai/v1'),
    novitaBaseUrl:      (fileConfig.novitaBaseUrl ?? env.NOVITA_BASE_URL ?? 'https://api.novita.ai/openai/v1'),
    ai21BaseUrl:        (fileConfig.ai21BaseUrl ?? env.AI21_BASE_URL ?? 'https://api.ai21.com/studio/v1'),

    // opencode.json style config
    permissions: fileConfig.permissions,
    agents: fileConfig.agents,
    mcpServers: fileConfig.mcpServers,
    theme: fileConfig.theme,
    keybinds: fileConfig.keybinds,
  };
}

export type ProviderName = 'openai' | 'gemini' | 'openrouter' | 'groq' | 'ollama' | 'qwen' |
  'deepseek' | 'zai' | 'moonshot' | 'anthropic' | 'mistral' | 'xai' |
  'cerebras' | 'together' | 'fireworks' | 'deepinfra' | 'siliconflow' |
  'nvidia' | 'cohere' | 'perplexity' | 'sambanova' | 'novita' | 'ai21';